import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";

const HERO_IMG =
    "https://images.pexels.com/photos/6077430/pexels-photo-6077430.jpeg";

export default function Hero() {
    return (
        <section
            data-testid="hero-section"
            className="relative min-h-[92vh] flex items-center overflow-hidden"
        >
            {/* Background image */}
            <div className="absolute inset-0">
                <img
                    src={HERO_IMG}
                    alt="Lady Justice – BM Juridik"
                    className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/90 to-[#0A0A0B]/40" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" />
            </div>

            {/* Content */}
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 w-full">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="overline"
                        >
                            Juristbyrå  ·  Sverige
                        </motion.p>

                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.15 }}
                            className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight"
                        >
                            Juridisk rådgivning
                            <br />
                            <span className="italic text-[#C4A977]">
                                med integritet
                            </span>{" "}
                            och skärpa.
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.3 }}
                            className="mt-8 text-base lg:text-lg text-[#A1A1AA] max-w-2xl leading-relaxed"
                        >
                            BM Juridik är en etablerad juristbyrå med bred
                            kompetens inom ett tiotal rättsområden — från
                            affärsjuridik och fastighetsrätt till arvsrätt,
                            migrationsrätt och brottmål. Vi är din betrodda
                            partner när det verkligen räknas.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.45 }}
                            className="mt-10 flex flex-wrap gap-4"
                        >
                            <Link
                                to="/kontakt"
                                data-testid="hero-cta-primary"
                                className="inline-flex items-center gap-3 px-7 py-4 bg-[#C4A977] text-black hover:bg-[#DBC08F] transition-all duration-300 rounded-sm font-medium text-sm tracking-wide group"
                            >
                                Boka konsultation
                                <ArrowRight
                                    size={16}
                                    className="transition-transform duration-300 group-hover:translate-x-1"
                                />
                            </Link>
                            <Link
                                to="/tjanster"
                                data-testid="hero-cta-secondary"
                                className="inline-flex items-center gap-3 px-7 py-4 border border-white/20 hover:border-[#C4A977] hover:text-[#C4A977] transition-all duration-300 rounded-sm text-sm tracking-wide"
                            >
                                Utforska rättsområden
                            </Link>
                        </motion.div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="lg:col-span-5 hidden lg:block"
                    >
                        <div className="relative bg-black/40 backdrop-blur-md border border-white/10 p-8 rounded-sm">
                            <p className="overline">Juridisk rådgivning</p>
                            <p className="mt-3 font-serif text-3xl text-[#F4F4F5] leading-tight">
                                Vi finns tillgängliga
                                <br />
                                <span className="text-[#C4A977]">online</span> — när det
                                passar dig.
                            </p>
                            <div className="hairline my-6" />
                            <p className="text-[#A1A1AA] text-sm leading-relaxed">
                                Möt en jurist via säker videolänk eller telefon.
                                Diskutera ditt ärende från hemmet, kontoret eller var
                                du än befinner dig — i hela Sverige.
                            </p>
                            <Link
                                to="/kontakt"
                                data-testid="hero-card-cta"
                                className="mt-6 inline-flex items-center gap-2 text-[#C4A977] hover:text-[#DBC08F] text-sm group"
                            >
                                Boka onlinemöte
                                <ArrowRight
                                    size={14}
                                    className="transition-transform duration-300 group-hover:translate-x-1"
                                />
                            </Link>

                            <div className="mt-6 pt-5 border-t border-[#C4A977]/20 flex items-start gap-3">
                                <Sparkles
                                    size={16}
                                    className="text-[#C4A977] mt-0.5 flex-shrink-0"
                                />
                                <p className="text-xs text-[#F4F4F5]/85 leading-relaxed">
                                    <span className="font-semibold text-[#C4A977]">
                                        Kostnadsfri inledande rådgivning
                                    </span>{" "}
                                    vid vårdnadstvister.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Scroll indicator */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
                <div className="w-px h-12 bg-gradient-to-b from-transparent via-[#C4A977]/60 to-transparent animate-pulse" />
            </div>
        </section>
    );
}
